import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { Link } from 'react-router-dom';

import { StarFilled, PhoneOutlined, MailOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Row, Col, Button, Tabs, Tooltip } from 'antd';

import ProductCard from 'components/ProductCard';
import Spin from 'components/Spin';
import { formatDate } from 'helpers/date';

import ModalEvaluation from './components/ModalEvaluation/index';
import { useShowCase } from './useShowCase';

interface IProps {
  match?: any;
}

const ProducerDetail: React.FC<IProps> = ({ match }) => {
  const [openModalEvaluation, setOpenModalEvaluation] = useState(false);
  const [producerId, setProducerId] = useState<any>(0);

  const { data, isFetching } = useShowCase(producerId);

  useEffect(() => {
    (async () => {
      const { producerId: id } = match.params;
      setProducerId(id);
    })();
  }, [match.params]);

  const handleOpenModalEvaluation = useCallback(() => {
    setOpenModalEvaluation(true);
  }, []);

  const handleCloseModalEvaluation = useCallback(() => {
    setOpenModalEvaluation(false);
  }, []);

  const tabItems = [
    {
      key: 'produtos',
      label: 'Produtos',
      children: (
        <div className='max-h-[600px] min-h-[350px] overflow-x-hidden overflow-y-auto p-4 pt-0 leading-normal'>
          <Row gutter={[16, 16]}>
            {data?.products?.map((item: any) => (
              <Col xs={24} sm={12} md={8} key={item.id}>
                <ProductCard data={item} />
              </Col>
            ))}
          </Row>
        </div>
      )
    },
    {
      key: 'afiliacoes',
      label: 'Afiliações',
      children: (
        <div className='max-h-[600px] min-h-[350px] overflow-x-hidden overflow-y-auto p-4 pt-0 leading-normal'>
          <Row gutter={[16, 16]}>
            {data?.affiliations?.map((item: any) => (
              <Col key={item.id} xs={24} sm={12} md={8}>
                <ProductCard data={item} />
              </Col>
            ))}
          </Row>
        </div>
      )
    }
  ];

  return (
    <>
      {isFetching && <Spin />}
      {!isFetching && !data && 'Perfil não encontrado'}
      {!isFetching && data && (
        <Fragment>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={4}>
              <div className='text-center'>
                <img
                  src={data.url_image}
                  className='mx-auto mb-8 block h-auto w-full max-w-[200px] overflow-hidden rounded-sm shadow-xs'
                  alt='details'
                />
                <h1 className='text-primary text-lg leading-tight font-medium'>{data.name}</h1>
                <div className='mt-4' />
                <div>
                  <span className='mb-1 flex items-center justify-center text-[32px] leading-none'>
                    <StarFilled className='mr-1 text-[#FABB0A]' style={{ fontSize: 'inherit' }} />
                    <FormattedNumber maximumFractionDigits={1} value={data.stars} />
                  </span>

                  <Button type='text' onClick={handleOpenModalEvaluation}>
                    Avaliar produtor
                  </Button>
                </div>

                {data.chat_with !== 0 && (
                  <Link to={'/producer/messages/' + data.chat_with}>
                    <Button type='primary'>Iniciar conversa</Button>
                  </Link>
                )}

                {data.show_contact && data.is_affiliate && (
                  <div className='leading-tight md:text-left'>
                    {data.phone && (
                      <div>
                        <PhoneOutlined style={{ marginRight: '0.5rem' }} />
                        {data.phone}
                      </div>
                    )}
                    {data.email && (
                      <div>
                        <MailOutlined style={{ marginRight: '0.5rem' }} />
                        {data.email}
                      </div>
                    )}
                    {data.skype && (
                      <div>
                        <VideoCameraOutlined style={{ marginRight: '0.5rem' }} />
                        Skype: {data.skype}
                      </div>
                    )}
                  </div>
                )}

                {(data?.badges || []).length > 0 && (
                  <>
                    <span className='text-primary block py-8 font-medium'>Conquistas desse produtor</span>
                    <div className='flex flex-wrap items-start justify-start'>
                      {data?.badges.map((badge: any) => (
                        <React.Fragment key={'detailbadgeprd-' + badge.id}>
                          <Tooltip
                            title={
                              <div>
                                {badge.name}: {badge.hint} <br /> Conquistado em{' '}
                                {formatDate(badge.qualified_at, 'dd/MM/yyyy')}
                              </div>
                            }
                            placement='top'
                          >
                            <div className='mr-4 mb-4 block w-[calc(100%/4-8px)] max-w-[80px] [&>img]:h-auto [&>img]:w-full'>
                              {badge.has_plus ? (
                                <img
                                  src={`https://cdn.eduzzcdn.com/badges/${badge.badge_elite}-plus.svg`}
                                  alt={`Badge ${badge.name}`}
                                />
                              ) : (
                                <img src={`https://cdn.eduzzcdn.com/badges/${badge.badge_elite}.svg`} alt='badgeimg' />
                              )}
                            </div>
                          </Tooltip>
                        </React.Fragment>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </Col>
            <Col xs={24} md={14}>
              <Tabs items={tabItems} />
            </Col>
            <Col xs={24} md={6}>
              <div className='w-full rounded-sm bg-white shadow-xs'>
                <h2 className='text-primary p-4 text-sm leading-none font-semibold uppercase'>Público-alvo</h2>
                <div className='max-h-[600px] w-full overflow-x-hidden overflow-y-auto px-4 pb-4'>
                  {Object.keys(data.target || {}).map((category: any, indexcat: any) => (
                    <div
                      className='[&>strong]:text-primary [&>span]:text-primary-light mb-4 text-sm [&>span]:block [&>strong]:mb-1 [&>strong]:block'
                      key={'target-' + indexcat}
                    >
                      <strong>{category}</strong>
                      {data.target[category]?.map((response: any, indexresp: any) => (
                        <span key={'targetresp-' + indexresp}>{response}</span>
                      ))}
                    </div>
                  ))}
                  {Object.keys(data.target || {}).length === 0 && 'Não possui público alvo'}
                </div>
              </div>
            </Col>
          </Row>
          <ModalEvaluation open={openModalEvaluation} producerId={producerId} onClose={handleCloseModalEvaluation} />
        </Fragment>
      )}
    </>
  );
};

export default ProducerDetail;
